import { ArrowCircleLeftRounded, StopCircleRounded } from "@mui/icons-material";
import { cn } from "@utils";
import { Paperclip } from "lucide-react";
import { FormEvent } from "react";

import { ModelConfig, ReasoningEffort } from "@config";
import { Device, SkillMetadata, TaskState } from "@models";
import { TemplateConfig } from "@types";
import {
  ContentBlock,
  ContentBlocksPreview,
  DocumentContentBlock,
  TaskContentBlock,
} from "src/components/Chat/ContentBlocksPreview";
import { CHAT_SUPPORTED_FILE_TYPES } from "src/components/Chat/utils";
import { ChatSkillsMenu } from "src/components/Skills/Chat/ChatSkillsMenu";
import { useGetDocumentReferences, useGetUserDataKeys } from "src/hooks/queries";
import { ChatAttachment, attachmentPreviews } from "./attachments";
import { ModelSelector } from "./ModelSelector";
import { TooltipIconButton } from "./TooltipIconButton";
import { ChatSkillContext } from "./types";
import { useChatDeviceContext } from "./useChatDeviceContext";

const EMPTY_UPLOADED_FILES: ChatAttachment[] = [];
const EMPTY_TASKS: TaskState[] = [];

interface ChatInputProps {
  input: string;
  setInput: (input: string) => void;
  contentBlocks: ContentBlock[];
  isLoading: boolean;
  isSubmitDisabled?: boolean;
  onSubmit: (e: FormEvent) => void;
  onStop: () => void;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePaste: (
    e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  dropRef: React.RefObject<HTMLDivElement>;
  dragOver: boolean;
  templatesConfig: TemplateConfig;
  formRef: React.RefObject<HTMLFormElement>;
  handleRemoveBlock: (idx: number) => void;
  handleAddDocument: (documentBlock: DocumentContentBlock) => void;
  handleAddTask?: (taskBlock: TaskContentBlock) => void;
  selectedModelConfig: ModelConfig;
  onModelSelect: (modelConfig: ModelConfig) => void;
  selectedReasoningEffort?: ReasoningEffort;
  onReasoningEffortSelect?: (reasoningEffort: ReasoningEffort) => void;
  uploadedFiles?: ChatAttachment[];
  onRemoveAttachment?: (id: string) => void;
  onRetryAttachment?: (id: string) => void;
  onSaveAttachmentChange?: (id: string, saveToEqms: boolean) => void;
  attachmentError?: string;
  tasks?: TaskState[];
  devices: Pick<Device, "id" | "name">[];
  attachedSkills: SkillMetadata[];
  skillContexts?: ChatSkillContext[];
  onAttachedSkillsChange: (skills: SkillMetadata[]) => void;
  onCreateSkillFromChat: () => void;
  isCreateSkillFromChatDisabled?: boolean;
}

export function ChatInput({
  input,
  setInput,
  contentBlocks,
  isLoading,
  isSubmitDisabled = false,
  onSubmit,
  onStop,
  handleFileUpload,
  handlePaste,
  dropRef,
  dragOver,
  templatesConfig,
  formRef,
  handleRemoveBlock,
  handleAddDocument,
  handleAddTask,
  selectedModelConfig,
  onModelSelect,
  selectedReasoningEffort,
  onReasoningEffortSelect,
  uploadedFiles = EMPTY_UPLOADED_FILES,
  onRemoveAttachment,
  onRetryAttachment,
  onSaveAttachmentChange,
  attachmentError,
  tasks = EMPTY_TASKS,
  devices,
  attachedSkills,
  skillContexts = [],
  onAttachedSkillsChange,
  onCreateSkillFromChat,
  isCreateSkillFromChatDisabled = isLoading,
}: ChatInputProps) {
  const { orgId } = useChatDeviceContext();
  const { data: documents } = useGetDocumentReferences(orgId);
  const { data: availableDataKeys = [] } = useGetUserDataKeys(!!orgId);

  const hasFilesPending = uploadedFiles.some((file) => file.status !== "ready");

  return (
    <form onSubmit={onSubmit} className="mx-auto w-full" ref={formRef}>
      <div
        ref={dropRef}
        className={cn(
          "bg-white relative  mx-auto w-full border-gray-200 border-solid rounded-lg shadow-xs transition-all flex flex-col p-3",
          dragOver
            ? "border-primary border-2 border-dotted"
            : "border border-solid"
        )}
      >
        <div className="flex-1">
          <div>
            {documents && (
              <>
                <ContentBlocksPreview
                  blocks={contentBlocks}
                  attachments={attachmentPreviews(uploadedFiles)}
                  onRemoveAttachment={onRemoveAttachment}
                  onRetryAttachment={onRetryAttachment}
                  onSaveAttachmentChange={onSaveAttachmentChange}
                  disabled={isLoading || isSubmitDisabled}
                  onRemove={handleRemoveBlock}
                  onAddDocument={handleAddDocument}
                  onAddTask={handleAddTask}
                  availableDataKeys={availableDataKeys}
                  templatesConfig={templatesConfig}
                  documents={documents}
                  devices={devices}
                  tasks={tasks}
                  attachedSkills={attachedSkills}
                  skillContexts={skillContexts}
                  onAttachedSkillsChange={onAttachedSkillsChange}
                  extraActions={
                    <ChatSkillsMenu
                      orgId={orgId}
                      attachedSkills={attachedSkills}
                      onAttachedSkillsChange={onAttachedSkillsChange}
                      onCreateSkillFromChat={onCreateSkillFromChat}
                      isCreateSkillFromChatDisabled={
                        isCreateSkillFromChatDisabled
                      }
                    />
                  }
                />
              </>
            )}
            {attachmentError && (
              <p role="alert" className="text-sm text-red-600">
                {attachmentError}
              </p>
            )}
            {/* Keep this native: MUI TextareaAutosize forced repeated layout reads in Terminal traces. */}
            <textarea
              disabled={isLoading || isSubmitDisabled}
              value={input}
              rows={1}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setInput(e.target.value)
              }
              onPaste={(e: React.ClipboardEvent<HTMLTextAreaElement>) =>
                handlePaste(e)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.metaKey &&
                  !e.nativeEvent.isComposing
                ) {
                  e.preventDefault();
                  const el = e.target as HTMLElement | undefined;
                  const form = el?.closest("form");
                  form?.requestSubmit();
                }
              }}
              placeholder="Type your message..."
              className="block max-h-[calc(14*1.5rem+1rem)] w-full resize-none overflow-y-auto border-0 bg-transparent px-0 py-2 font-[inherit] text-base font-normal leading-6 text-inherit outline-none [field-sizing:content] placeholder:text-gray-400 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex justify-between gap-2">
          <ModelSelector
            selectedModelConfig={selectedModelConfig}
            onModelSelect={onModelSelect}
            selectedReasoningEffort={selectedReasoningEffort}
            onReasoningEffortSelect={onReasoningEffortSelect}
          />

          <div className="flex items-center gap-4">
            <TooltipIconButton
              tooltip="Upload file"
              aria-label="Upload file"
              disabled={isLoading || isSubmitDisabled}
              onClick={() => {
                const input = document.getElementById(
                  "file-input"
                ) as HTMLInputElement;
                input.click();
              }}
              size="large"
            >
              <Paperclip className="text-gray-500 size-[16px]" />
            </TooltipIconButton>

            {isLoading ? (
              <TooltipIconButton tooltip="Stop" onClick={onStop} size="large">
                {/* adding a rotation animation around the stop circle*/}
                <StopCircleRounded className="size-9" />
              </TooltipIconButton>
            ) : (
              <TooltipIconButton
                type="submit"
                tooltip="Send"
                aria-label="Send"
                onClick={onSubmit}
                disabled={
                  isLoading ||
                  isSubmitDisabled ||
                  hasFilesPending ||
                  !!attachmentError ||
                  (!input.trim() &&
                    contentBlocks.length === 0 &&
                    uploadedFiles.length === 0)
                }
                color="primary"
                // size="small"
                size="large"
              >
                <ArrowCircleLeftRounded className="rotate-90 size-9" />
              </TooltipIconButton>
            )}
          </div>
        </div>

        <input
          id="file-input"
          type="file"
          disabled={isLoading || isSubmitDisabled}
          onChange={handleFileUpload}
          accept={CHAT_SUPPORTED_FILE_TYPES}
          className="hidden"
          multiple
        />
      </div>
    </form>
  );
}
